import React from 'react';
import { Box, Heading, Loader, Text } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { SparklesSmall } from '@wix/wix-ui-icons-common';
import { useAtom } from 'jotai';
import { isEmpty } from 'lodash';
import { dataHooks } from '../../datahooks';
import { ButtonGrid } from '../../../../components/ButtonGrid';
import { selectedSuggestionAtom, suggestionsAtom } from '../../atoms';
import { useSuggestStoreName } from '../../api/mutations';
import { normalizeProducts } from '../../utils';
import { useStoreNameInput } from '../../hooks/useStoreNameInput';
import { useStoreName, useSelectedCategory } from '../../../../store/appStore';
import s from './StoreNameMain.scss';
import { StoreNameInput } from '../../components/StoreNameInput';

export function StoreNameMain() {
  const { t } = useTranslation();
  const [selectedSuggestion] = useAtom(selectedSuggestionAtom);
  const [suggestions, setSuggestions] = useAtom(suggestionsAtom);
  const storeName = useStoreName();
  const selectedCategory = useSelectedCategory();
  const { handleStoreNameChange, handleSuggestionSelect } = useStoreNameInput();

  const { mutate: fetchSuggestions, isPending: isLoadingSuggestions } = useSuggestStoreName();

  function handleGetSuggestions() {
    if (!selectedCategory) {
      return;
    }

    fetchSuggestions(
      {
        category: selectedCategory,
        products: normalizeProducts(selectedCategory),
        previousResults: suggestions,
      },
      {
        onSuccess: (data) => {
          setSuggestions(data);
        },
      },
    );
  }

  const suggestionItems = suggestions.map((suggestion) => ({
    key: suggestion,
    value: suggestion,
    label: suggestion,
    onClick: (value: string) => handleSuggestionSelect({ value, label: value }),
    dataHook: `${dataHooks.StoreNameSuggestion}-${suggestion}`,
  }));

  if (isLoadingSuggestions && isEmpty(suggestions)) {
    return (
      <Box align="center" verticalAlign="middle" height="100%" width="100%">
        <Loader size="large" />
      </Box>
    );
  }

  return (
    <Box direction="vertical" height="100%" width="100%" gap={4} dataHook={dataHooks.StoreNameMainContent}>
      <Box direction="vertical" gap={1}>
        <Heading className={s.textTitle} size="medium">
          {t('WHAT_YOUR_STORE_NAME')}
        </Heading>

        <Text className={s.textSubtitle}>{t('PICK_MEMORABLE_NAME_TO_REPRESENT_YOUR_BRAND')}</Text>
      </Box>

      <StoreNameInput value={storeName} onChange={handleStoreNameChange} />

      <Box direction="vertical" gap={4}>
        <Box align="space-between" verticalAlign="middle">
          <Box direction="vertical" gap={1}>
            <Text weight="bold">{t('store_name.ai-suggestions.title')}</Text>

            <Text size="tiny" secondary>
              {t('ai_disclaimer')}
            </Text>
          </Box>

          <button
            onClick={handleGetSuggestions}
            className={s.generateButton}
            data-hook={`${dataHooks.StoreNameSuggestion}-generate`}
          >
            <Box align="center" gap={1} verticalAlign="middle">
              <SparklesSmall />

              <Text>{t('GENERATE_NEW_SUGGESTIONS')}</Text>
            </Box>
          </button>
        </Box>

        <ButtonGrid
          items={suggestionItems}
          selectedValue={selectedSuggestion}
          isDisabled={isLoadingSuggestions}
        />
      </Box>
    </Box>
  );
}
